import React, { useMemo, useState } from 'react';

/**
 * Read-only Credit Snapshot.
 *
 * Used inside the Credit Approval Screen so a Credit Approver can see the same
 * credit picture a trader sees on the CRM tab, without any of the editing
 * controls. Intentionally a thin presentational component — no fetching,
 * no auto-save, no dropdowns. Pass it the data, it renders.
 *
 * Expected `profile` shape (subset of CRMProfile + computed credit_balance):
 *   {
 *     credit_limit, partner_credit_days, credit_risk_rating,
 *     credit_status, credit_exceed_level,
 *     enquiry_credit_amount, pending_credit_amount, actualized_credit_amount,
 *     credit_balance,
 *   }
 */

const SEGMENT_META = [
    {
        key: 'enquiry',
        label: 'Enquiry',
        description: 'Open enquiries at Bid / Offer stage',
    },
    {
        key: 'pending',
        label: 'Pending',
        description: 'Signed deals awaiting BL / BDN delivery',
    },
    {
        key: 'actualized',
        label: 'Actualized',
        description: 'Delivered, awaiting invoice payment',
    },
    {
        key: 'balance',
        label: 'Available Balance',
        description: 'Headroom remaining on the credit limit',
    },
];

const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const CRMCreditSnapshot = ({ profile, requestedIncrease = null }) => {
    const [hoveredSegment, setHoveredSegment] = useState(null);

    const limitNum = toNumber(profile?.credit_limit);
    const enquiryNum = toNumber(profile?.enquiry_credit_amount);
    const pendingNum = toNumber(profile?.pending_credit_amount);
    const actualizedNum = toNumber(profile?.actualized_credit_amount);

    const computedBalance = useMemo(() => {
        if (profile?.credit_balance !== undefined && profile?.credit_balance !== null) {
            return Number(profile.credit_balance);
        }
        if (profile?.credit_limit === null || profile?.credit_limit === undefined) {
            return null;
        }
        return limitNum - (enquiryNum + pendingNum + actualizedNum);
    }, [profile, limitNum, enquiryNum, pendingNum, actualizedNum]);

    const balanceNum = computedBalance ?? 0;
    const usedNum = enquiryNum + pendingNum + actualizedNum;

    const pct = (value) => {
        if (limitNum <= 0) return 0;
        return Math.max(0, Math.min(100, (Math.max(0, value) / limitNum) * 100));
    };

    const enquiryPct = pct(enquiryNum);
    const pendingPct = pct(pendingNum);
    const actualizedPct = pct(actualizedNum);
    const balancePct = Math.max(0, 100 - enquiryPct - pendingPct - actualizedPct);
    const usedPct = Math.min(100, enquiryPct + pendingPct + actualizedPct);

    const balanceIsNegative = computedBalance !== null && computedBalance < 0;

    // Credit Exceed (PM request): dollar amount of credit used beyond the
    // Credit Limit - i.e. credit requested for deals that has not yet been
    // formally approved as a limit increase. Mirrors CRMCreditRatingTab.
    const overLimitAmount = balanceIsNegative ? Math.abs(computedBalance) : 0;

    const segmentValues = {
        enquiry: { amount: enquiryNum, pct: enquiryPct, offset: 0 },
        pending: { amount: pendingNum, pct: pendingPct, offset: enquiryPct },
        actualized: {
            amount: actualizedNum,
            pct: actualizedPct,
            offset: enquiryPct + pendingPct,
        },
        balance: {
            amount: balanceNum,
            pct: balancePct,
            offset: enquiryPct + pendingPct + actualizedPct,
        },
    };

    const projectedLimit =
        requestedIncrease !== null && requestedIncrease !== undefined
            ? limitNum + Number(requestedIncrease)
            : null;

    return (
        <div className="crm-credit-snapshot">
            <div className="crm-credit-snapshot-grid">
                <div className="crm-credit-snapshot-cell">
                    <span className="crm-credit-snapshot-label">Credit Limit</span>
                    <span className="crm-credit-snapshot-value">
                        {limitNum > 0 ? formatCurrency(limitNum) : '-'}
                    </span>
                </div>
                <div className="crm-credit-snapshot-cell">
                    <span className="crm-credit-snapshot-label">Partner Credit Days</span>
                    <span className="crm-credit-snapshot-value">
                        {profile?.partner_credit_days ?? '-'}
                    </span>
                </div>
                <div className="crm-credit-snapshot-cell">
                    <span className="crm-credit-snapshot-label">Risk Rating</span>
                    <span className="crm-credit-snapshot-value">
                        {profile?.credit_risk_rating || '-'}
                    </span>
                </div>
                <div className="crm-credit-snapshot-cell">
                    <span className="crm-credit-snapshot-label">Credit Exceed</span>
                    <span
                        className="crm-credit-snapshot-value"
                        data-negative={overLimitAmount > 0 ? 'true' : 'false'}
                    >
                        {overLimitAmount > 0 ? formatCurrency(overLimitAmount) : '-'}
                    </span>
                </div>
                <div className="crm-credit-snapshot-cell">
                    <span className="crm-credit-snapshot-label">Credit Status</span>
                    <span
                        className="crm-credit-snapshot-value"
                        data-status={profile?.credit_status || 'pending'}
                    >
                        {(profile?.credit_status || 'pending').toUpperCase()}
                    </span>
                </div>
                <div className="crm-credit-snapshot-cell">
                    <span className="crm-credit-snapshot-label">Available Balance</span>
                    <span
                        className="crm-credit-snapshot-value"
                        data-negative={balanceIsNegative ? 'true' : 'false'}
                    >
                        {computedBalance === null ? '-' : formatCurrency(computedBalance)}
                    </span>
                </div>
                {projectedLimit !== null && (
                    <div className="crm-credit-snapshot-cell crm-credit-snapshot-cell--highlight">
                        <span className="crm-credit-snapshot-label">
                            Projected Limit (if approved)
                        </span>
                        <span className="crm-credit-snapshot-value">
                            {formatCurrency(projectedLimit)}
                        </span>
                    </div>
                )}
            </div>

            <div
                className="crm-credit-allocation"
                onMouseLeave={() => setHoveredSegment(null)}
            >
                <div className="crm-credit-allocation-header">
                    <div>
                        <h3 className="crm-credit-allocation-title">
                            Credit Allocation Breakdown
                        </h3>
                        <p className="crm-credit-allocation-subtitle">
                            Hover a segment for details.
                        </p>
                    </div>
                    <div className="crm-credit-allocation-summary">
                        <div className="crm-credit-allocation-summary-item">
                            <span className="crm-credit-allocation-summary-label">Used</span>
                            <span className="crm-credit-allocation-summary-value">
                                {formatCurrency(usedNum)}
                            </span>
                        </div>
                        <div className="crm-credit-allocation-summary-divider" />
                        <div className="crm-credit-allocation-summary-item">
                            <span className="crm-credit-allocation-summary-label">Limit</span>
                            <span className="crm-credit-allocation-summary-value">
                                {limitNum > 0 ? formatCurrency(limitNum) : '-'}
                            </span>
                        </div>
                        <div className="crm-credit-allocation-summary-divider" />
                        <div className="crm-credit-allocation-summary-item">
                            <span className="crm-credit-allocation-summary-label">
                                Utilised
                            </span>
                            <span
                                className="crm-credit-allocation-summary-value"
                                data-state={
                                    balanceIsNegative
                                        ? 'over'
                                        : usedPct >= 80
                                        ? 'warning'
                                        : 'ok'
                                }
                            >
                                {limitNum > 0 ? `${usedPct.toFixed(1)}%` : '-'}
                            </span>
                        </div>
                    </div>
                </div>

                <div
                    className="crm-credit-progress-bar"
                    role="progressbar"
                    aria-label="Credit allocation breakdown"
                    aria-valuemin="0"
                    aria-valuemax={limitNum > 0 ? limitNum : 1}
                    aria-valuenow={usedNum}
                >
                    {SEGMENT_META.map((seg) => {
                        const v = segmentValues[seg.key];
                        return (
                            <div
                                key={seg.key}
                                className={`crm-credit-progress-segment crm-credit-progress-segment--${seg.key}`}
                                style={{ left: `${v.offset}%`, width: `${v.pct}%` }}
                                data-active={hoveredSegment === seg.key ? 'true' : 'false'}
                                data-dimmed={
                                    hoveredSegment !== null && hoveredSegment !== seg.key
                                        ? 'true'
                                        : 'false'
                                }
                                onMouseEnter={() => setHoveredSegment(seg.key)}
                                title={`${seg.label}: ${formatCurrency(v.amount)} (${v.pct.toFixed(1)}%)`}
                            />
                        );
                    })}
                </div>

                <div className="crm-credit-progress-legend">
                    {SEGMENT_META.map((seg) => {
                        const v = segmentValues[seg.key];
                        return (
                            <div
                                key={seg.key}
                                className="crm-credit-progress-legend-item"
                                data-segment={seg.key}
                                data-active={hoveredSegment === seg.key ? 'true' : 'false'}
                                onMouseEnter={() => setHoveredSegment(seg.key)}
                                onMouseLeave={() => setHoveredSegment(null)}
                            >
                                <span
                                    className="crm-credit-progress-legend-swatch"
                                    data-segment={seg.key}
                                />
                                <span className="crm-credit-progress-legend-body">
                                    <span className="crm-credit-progress-legend-name">
                                        {seg.label}
                                    </span>
                                    <span className="crm-credit-progress-legend-amount">
                                        {formatCurrency(v.amount)}
                                    </span>
                                    <span className="crm-credit-progress-legend-pct">
                                        {limitNum > 0 ? `${v.pct.toFixed(1)}%` : '-'}
                                    </span>
                                    <span className="crm-credit-progress-legend-desc">
                                        {seg.description}
                                    </span>
                                </span>
                            </div>
                        );
                    })}
                </div>

                {limitNum <= 0 && (
                    <small className="crm-credit-progress-note">
                        Credit Limit is not set for this partner.
                    </small>
                )}
            </div>
        </div>
    );
};

export default CRMCreditSnapshot;
